/**
 * The admin API, as this panel uses it.
 *
 * ── Why it goes through the ordinary `request` ───────────────────────────────
 *
 * No `x-admin-token`, no `x-api-key`. `AdminAccessGuard` gained a third path —
 * the operator's own `gamer_id` session cookie, when their account is an ADMIN —
 * precisely so a panel shipped inside the game needs no second credential. A
 * shared secret embedded in client code is not a secret, which is why Kottster
 * had to be a separate server-side application to hold one.
 *
 * So every call here is the app's normal `request()`: same cookie, same 401
 * refresh interceptor, same error type. The panel is signed-in-ness plus a role,
 * and nothing else.
 *
 * ── What is deliberately not here ────────────────────────────────────────────
 *
 * Kottster shipped eleven generic Postgres table browsers. Eight of those tables
 * — payments, currency ledger, quests, user quests, seasons, season rewards,
 * chat messages, matches and inventory — have **no REST endpoint**, so they are
 * not in this panel and were not replaced. That was a scoping decision, not an
 * oversight: browsing them now means `psql`, or building the endpoints. Written
 * down here because the absence is otherwise indistinguishable from a bug.
 */
import { ApiError, request } from '@/net/api';

const API_BASE = import.meta.env.VITE_API_URL || '';

/**
 * A multipart upload, not `request()` — that helper unconditionally sets
 * `Content-Type: application/json` whenever a body is present, which would
 * strip the boundary a `FormData` body needs. Same cookie and error shape as
 * `request()` otherwise; no 401-refresh retry, since an admin upload failing
 * once on an expired session is rare enough that a manual retry is fine and
 * the retry logic isn't worth duplicating for one call site.
 */
async function uploadFile<T>(path: string, file: File): Promise<T> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    body: form,
    credentials: 'include',
  });
  if (!response.ok) {
    let message = response.status >= 500 ? 'The server had a problem.' : 'That request was refused.';
    try {
      const body = (await response.json()) as { message?: unknown };
      if (typeof body.message === 'string') message = body.message;
      else if (Array.isArray(body.message)) message = String(body.message[0]);
    } catch {
      // Fall through to the generic message above.
    }
    throw new ApiError(message, response.status);
  }
  return (await response.json()) as T;
}

// ─── Overview ────────────────────────────────────────────────────────────────

export interface AdminStats {
  totalUsers: number;
  newToday: number;
  newThisWeek: number;
  pendingWithdrawals: number;
  pendingKyc: number;
}

/**
 * `GET /api/admin/economy-stats` — a second, game-owned endpoint, not part of
 * `AdminStats` above. Core's `GET /api/admin/stats` (backing `AdminStats`) is
 * template-owned and untouched; these three numbers have no home there, so the
 * Overview pane fetches both and merges them. See `ADMIN_API_CHANGELOG.md`'s
 * 2026-09-03 entry for why the split exists.
 */
export interface EconomyStats {
  onlineNow: number;
  coinsInEconomy: number;
  diamondsInEconomy: number;
}

export interface ServerStatus {
  [key: string]: unknown;
}

// ─── Players ─────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  email: string;
  username: string | null;
  role: string;
  banned: boolean;
  emailVerified: boolean;
  createdAt: string;
  lastSeenAt: string | null;
  softBalance: number;
  premiumBalance: number;
  level: number;
  kycStatus: string;
}

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

// ─── Content ─────────────────────────────────────────────────────────────────

export interface NewsRow {
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  imageUrl?: string | null;
  published: boolean;
  createdAt: string;
}

export interface FaqRow {
  id: string;
  question: string;
  answer: string;
  active: boolean;
  sortOrder: number;
}

export interface LegalRow {
  id: string;
  slug: string;
  title: string;
  content: string;
  active: boolean;
  updatedAt: string;
}

export interface SocialRow {
  id: string;
  platform: string;
  url: string;
  icon: string;
  active: boolean;
  sortOrder: number;
}

// ─── Billing ─────────────────────────────────────────────────────────────────

export interface AdminInvoice {
  id: string;
  number: string;
  type: string;
  amountCents: number;
  vatCents: number;
  totalCents: number;
  currency: string;
  description: string;
  status: string;
  createdAt: string;
  user?: { email: string; username: string | null } | null;
}

/**
 * A real-money transaction as the provider recorded it.
 *
 * **Not the same thing as an invoice**, and both are listed for that reason.
 * The `Invoice` is the document the game issued; this is the provider's own
 * charge — its transaction id, its status, and the rate the amount was
 * converted at. A refund, a failed capture or a currency mismatch shows here
 * and nowhere else.
 */
export interface PaymentRow {
  id: string;
  provider: string;
  providerTxId: string;
  /** Cents, in `currency`. */
  amount: number;
  currency: string;
  /** Cents in the base currency, or null when the payment was already in it. */
  amountBase: number | null;
  exchangeRate: number | null;
  status: string;
  createdAt: string;
  user?: { id: string; email: string; username: string | null } | null;
}

export interface WithdrawalRow {
  id: string;
  amountCents: number;
  feeCents: number;
  netCents: number;
  currency: string;
  status: string;
  payoutMethod: string | null;
  reason: string | null;
  createdAt: string;
  user?: { email: string; username: string | null } | null;
}

export interface KycRow {
  id: string;
  email: string;
  username: string | null;
  kycStatus: string;
  kycFullName: string | null;
  kycCountry: string | null;
  /** When the account submitted. Null for one that never has. */
  kycSubmittedAt: string | null;
  kycReviewedAt: string | null;
}

// ─── Keys ────────────────────────────────────────────────────────────────────

export interface ApiKeyRow {
  id: string;
  label: string;
  lastUsedAt: string | null;
  createdAt: string;
}

// ─── Payment methods ─────────────────────────────────────────────────────────

/**
 * One logo in the landing footer's payment-method strip. `iconUrl` is
 * server-computed (`${ICON_URL_PREFIX}${icon}.svg`) — the frontend never
 * builds it itself, so a future change to where icons are served only has to
 * change in one place.
 */
export interface PaymentMethodRow {
  slug: string;
  name: string;
  icon: string;
  enabled: boolean;
  sortOrder: number;
  iconUrl: string;
}

// ─── Core payments config (gateway method toggles) ──────────────────────────

/**
 * One row of core's `GET /api/admin/payments/config` — a gateway method
 * (card, BLIK, …) as opposed to a footer logo above. `configured` is whether
 * the terminal's credentials are present; the API never says *which*
 * credentials, and neither does this panel. `inactiveReason` is optional —
 * core may add it; when absent the panel derives a reason itself.
 */
export interface PaymentConfigMethod {
  id: string;
  label: string;
  configured: boolean;
  enabled: boolean;
  active: boolean;
  currencies: string[];
  inactiveReason?: string | null;
}

/** The subset of the config snapshot this panel reads. */
export interface PaymentsConfig {
  adopted: boolean;
  withdrawalsEnabled: boolean;
  deposit: PaymentConfigMethod[];
  payout: PaymentConfigMethod[];
}

export interface PaymentsConfigUpdate {
  withdrawalsEnabled?: boolean;
  deposit?: Record<string, { enabled: boolean }>;
  payout?: Record<string, { enabled: boolean }>;
}

/** `{ 'company.name': 'x' }` to `{ name: 'x' }`. See `company` below. */
function stripPrefix(values: Record<string, string>, prefix: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(values)) {
    out[key.startsWith(prefix) ? key.slice(prefix.length) : key] = value;
  }
  return out;
}

const qs = (params: Record<string, string | number | undefined>): string => {
  const out = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') out.set(k, String(v));
  }
  const s = out.toString();
  return s ? `?${s}` : '';
};

export const adminApi = {
  stats: () => request<AdminStats>('/api/admin/stats'),
  economyStats: () => request<EconomyStats>('/api/admin/economy-stats'),
  serverStatus: () => request<ServerStatus>('/api/admin/server-status'),

  users: (params: { page?: number; search?: string; status?: string }) =>
    request<Page<AdminUser>>(`/api/admin/users${qs(params)}`),
  /**
   * `GET /api/admin/players` — a game-owned parallel of `users()` above, with
   * one extra filter core's list has no way to gain from outside it. Used by
   * the Players pane; `users()` above stays reachable for parity/testing but
   * the pane itself calls this one. See `ADMIN_API_CHANGELOG.md`.
   */
  players: (params: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: string;
    kycStatus?: string;
  }) => request<Page<AdminUser>>(`/api/admin/players${qs(params)}`),
  /**
   * `PATCH /api/admin/players/:id/kyc-status` — sets the state directly; core
   * has no equivalent (`verifyKyc` below only ever jumps to APPROVED).
   */
  setKycStatus: (id: string, status: 'NONE' | 'PENDING' | 'APPROVED' | 'REJECTED') =>
    request(`/api/admin/players/${id}/kyc-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),
  /**
   * `POST /api/admin/users/create` — core's own, existing endpoint, unused by
   * any frontend until now. Dates are never sent: `createdAt` is the server's,
   * stamped at creation.
   */
  createUser: (body: {
    username: string;
    email: string;
    password: string;
    emailVerified?: boolean;
    kycStatus?: string;
  }) => request<AdminUser>('/api/admin/users/create', { method: 'POST', body: JSON.stringify(body) }),
  ban: (id: string, reason: string) =>
    request(`/api/admin/users/${id}/ban`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),
  unban: (id: string) => request(`/api/admin/users/${id}/unban`, { method: 'POST' }),
  verifyEmail: (id: string) =>
    request(`/api/admin/users/${id}/verify-email`, { method: 'POST' }),
  verifyKyc: (id: string) => request(`/api/admin/users/${id}/verify-kyc`, { method: 'POST' }),

  /**
   * Sends the account the ordinary one-hour, single-use reset link.
   *
   * Deliberately not `POST :id/password`, which sets a password the operator
   * then has to convey to the player somehow. This one puts no credential in
   * anybody's hands — only the person reading that inbox can finish it.
   */
  sendPasswordReset: (id: string) =>
    request<{ success: boolean; sentTo: string }>(
      `/api/admin/users/${id}/send-password-reset`,
      { method: 'POST' },
    ),
  /**
   * `AddCreditsDto` is stricter than it looks and every field here is
   * load-bearing: `amount` is `@IsInt() @IsPositive()`, so this **only ever
   * adds** — there is no negative path and asking for one is a 400; `currency`
   * is required and is the enum `SOFT | PREMIUM`, not a code; and the note
   * field is `description`, not `reason`. Sending `{ amount, reason }`, which
   * is what a reading of the route name suggests, fails validation every time.
   */
  addCredits: (id: string, amount: number, currency: 'SOFT' | 'PREMIUM', description: string) =>
    request(`/api/admin/users/${id}/add-credits`, {
      method: 'POST',
      body: JSON.stringify({ amount, currency, description }),
    }),

  /**
   * **The odd one out: news is paginated, the other three collections are
   * not.** `GET /admin/news` answers `{ items, page, pageSize, total }` while
   * faq, legal and social-links each answer a bare array. Unwrapped here so
   * every collection reaches the panel as a list and no component has to know
   * which is which.
   */
  news: async () => (await request<Page<NewsRow>>('/api/admin/news')).items,
  newsCreate: (body: {
    title: string;
    summary: string;
    content: string;
    slug?: string;
    published?: boolean;
    publishedAt?: string;
  }) => request<NewsRow>('/api/admin/news', { method: 'POST', body: JSON.stringify(body) }),
  newsPatch: (id: string, body: Partial<NewsRow> & { publishedAt?: string | null }) =>
    request(`/api/admin/news/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /**
   * `PATCH /api/admin/cms-ext/news/:id/slug` — a supplement, not core's own
   * route. Core's create/patch always derive the slug from the title; this
   * sets a custom one as a second call, only when the operator changed it.
   */
  newsSlugSet: (id: string, slug: string) =>
    request(`/api/admin/cms-ext/news/${id}/slug`, {
      method: 'PATCH',
      body: JSON.stringify({ slug }),
    }),
  /**
   * `POST /api/admin/cms-ext/news/:id/image` — a supplement, not core's own
   * route: `News.imageUrl` is a plain string core's DTOs already accept, but
   * core has no upload endpoint to produce that URL from a file. The article
   * must already exist (create it first, then call this).
   */
  newsImageUpload: (id: string, file: File) =>
    uploadFile<NewsRow>(`/api/admin/cms-ext/news/${id}/image`, file),
  newsDelete: (id: string) => request(`/api/admin/news/${id}`, { method: 'DELETE' }),

  faq: async () => {
    const data = await request<{ items: FaqRow[]; total: number } | FaqRow[]>('/api/admin/faq');
    return Array.isArray(data) ? data : data.items;
  },
  faqPatch: (id: string, body: Partial<FaqRow>) =>
    request(`/api/admin/faq/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  faqCreate: (body: { question: string; answer: string }) =>
    request('/api/admin/faq', { method: 'POST', body: JSON.stringify(body) }),
  faqDelete: (id: string) => request(`/api/admin/faq/${id}`, { method: 'DELETE' }),

  legal: async () => {
    const data = await request<{ items: LegalRow[]; total: number } | LegalRow[]>('/api/admin/legal');
    return Array.isArray(data) ? data : data.items;
  },
  legalCreate: (body: { slug: string; title: string; content: string; active?: boolean }) =>
    request<LegalRow>('/api/admin/legal', { method: 'POST', body: JSON.stringify(body) }),
  legalPatch: (id: string, body: Partial<LegalRow>) =>
    request(`/api/admin/legal/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  /**
   * `PATCH /api/admin/cms-ext/legal/:id/updated-at` — a supplement. Core's
   * `legalPatch` above has no field for this; Prisma's `@updatedAt` only
   * auto-stamps when nothing explicit is passed, so this direct write lands
   * exactly the date given.
   */
  legalUpdatedAtSet: (id: string, updatedAt: string) =>
    request(`/api/admin/cms-ext/legal/${id}/updated-at`, {
      method: 'PATCH',
      body: JSON.stringify({ updatedAt }),
    }),

  social: async () => {
    const data = await request<{ items: SocialRow[]; total: number } | SocialRow[]>('/api/admin/social-links');
    return Array.isArray(data) ? data : data.items;
  },
  socialPatch: (id: string, body: Partial<SocialRow>) =>
    request(`/api/admin/social-links/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  socialCreate: (body: { platform: string; url: string; sortOrder?: number }) =>
    request('/api/admin/social-links', { method: 'POST', body: JSON.stringify(body) }),
  socialDelete: (id: string) =>
    request(`/api/admin/social-links/${id}`, { method: 'DELETE' }),

  payments: (params: { page?: number; search?: string; status?: string }) =>
    request<Page<PaymentRow> & { completedBaseCents: number | null }>(
      `/api/admin/payments-ledger${qs(params)}`,
    ),

  invoices: (params: { page?: number }) =>
    request<Page<AdminInvoice>>(`/api/admin/invoices${qs(params)}`),
  /**
   * `POST /api/admin/invoices-ext/generate` — a game-owned supplement beside
   * core's own `POST /api/admin/invoices/generate`, adding the fields the
   * "Generate invoice" dialog needs (`transactionId`, `status`, `itemCode`)
   * that core's route has no room for. See `ADMIN_API_CHANGELOG.md`'s
   * 2026-09-30 entry.
   */
  generateInvoice: (body: {
    userId: string;
    type: 'IN' | 'OUT';
    amountCents: number;
    description: string;
    vatRate?: number;
    currency?: string;
    recipientName?: string;
    recipientCompany?: string;
    recipientCode?: string;
    paymentMethod?: string;
    itemCode?: string;
    transactionId?: string;
    status?: 'PAID' | 'PENDING';
  }) => request<AdminInvoice>('/api/admin/invoices-ext/generate', {
    method: 'POST',
    body: JSON.stringify(body),
  }),
  withdrawals: (params: { page?: number; status?: string }) =>
    request<Page<WithdrawalRow> & { pendingTotalCents: number }>(`/api/admin/withdrawals${qs(params)}`),
  withdrawalApprove: (id: string, note?: string) =>
    request(`/api/admin/withdrawals/${id}/approve`, { method: 'POST', body: JSON.stringify({ note }) }),
  withdrawalReject: (id: string, reason: string) =>
    request(`/api/admin/withdrawals/${id}/reject`, { method: 'POST', body: JSON.stringify({ reason }) }),
  withdrawalCheckStatus: (id: string) =>
    request(`/api/admin/withdrawals/${id}/check-status`, { method: 'POST' }),
  kyc: (params: { page?: number }) => request<Page<KycRow>>(`/api/admin/kyc/users${qs(params)}`),
  kycApprove: (id: string) => request(`/api/admin/kyc/users/${id}/approve`, { method: 'POST' }),
  kycReject: (id: string, reason: string) =>
    request(`/api/admin/kyc/users/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  settings: () => request<Record<string, string>>('/api/admin/settings'),

  /**
   * ── The prefix is asymmetric, and that is the API's shape, not a bug here ──
   *
   * `GET /admin/settings/company` returns `settings.getAll('company.')`, so its
   * keys arrive **prefixed**: `company.name`. `POST` writes
   * `settings.set(\`company.\${key}\`)`, so it expects them **unprefixed**:
   * `name`. Sending back what the GET returned would write `company.company.name`
   * — a silent no-op that leaves the form looking saved and the invoice
   * letterhead unchanged.
   *
   * Both directions are normalised here, so the panel only ever handles bare
   * names and no caller has to remember which side it is on.
   */
  company: async () => stripPrefix(await request<Record<string, string>>('/api/admin/settings/company'), 'company.'),
  setCompany: (settings: Record<string, string>) =>
    request('/api/admin/settings/company', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    }),
  commissions: async () => stripPrefix(await request<Record<string, string>>('/api/admin/settings/commissions'), 'commission.'),
  setCommissions: (settings: Record<string, string>) =>
    request('/api/admin/settings/commissions', {
      method: 'POST',
      body: JSON.stringify({ settings }),
    }),

  meta: () => request<{ values: Record<string, string>; resolved?: Record<string, unknown> }>(
    '/api/admin/meta',
  ),
  /**
   * The keys are **unprefixed** — `title`, not `meta.title`. `META_SETTING_PREFIX`
   * is how they are stored in `game_settings`; the API speaks the bare names,
   * and sending a prefixed one is rejected as an unknown key. The body is
   * `{ settings }`, matching `UpdateMetaDto`.
   */
  setMeta: (settings: Record<string, string>) =>
    request('/api/admin/meta', { method: 'POST', body: JSON.stringify({ settings }) }),

  keys: () => request<ApiKeyRow[]>('/api/admin/api-keys'),
  createKey: (label: string) =>
    request<{ key: string }>('/api/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify({ label }),
    }),
  deleteKey: (id: string) => request(`/api/admin/api-keys/${id}`, { method: 'DELETE' }),

  /** All 88 shipped logos, enabled or not — the toggle is the only editor. */
  paymentMethods: () => request<PaymentMethodRow[]>('/api/admin/payment-methods'),
  togglePaymentMethod: (slug: string, enabled: boolean) =>
    request('/api/admin/payment-methods/toggle', {
      method: 'POST',
      body: JSON.stringify({ slug, enabled }),
    }),

  /** Core's gateway-method config (`GET/PATCH /api/admin/payments/config`). */
  paymentsConfig: () => request<PaymentsConfig>('/api/admin/payments/config'),
  updatePaymentsConfig: (update: PaymentsConfigUpdate) =>
    request<PaymentsConfig>('/api/admin/payments/config', {
      method: 'PATCH',
      body: JSON.stringify(update),
    }),
};
